import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const plans = [
  {
    id: 1,                    // ← Matches your plans table
    name: 'Starter',
    price: 250,
    billing: 'Per Term',
    popular: false,
    features: [
      'Up to 500 Students',
      'Up to 20 Teachers',
      '1 School Administrator',
      'Student Management',
      'Teacher Management',
      'Class Management',
      'Attendance Tracking',
      'Exam & Results',
      'Timetable',
      'Parent Portal',
      'Basic Reports',
    ],
  },
  {
    id: 2,
    name: 'Basic',
    price: 500,
    billing: 'Per Term',
    popular: true,
    features: [
      'Up to 750 Students',
      'Up to 30 Teachers',
      '2 School Administrators',
      'Everything in Starter',
      'Finance Module',
      'Library Management',
      'Inventory Management',
      'Improved Reports',
      'Email & WhatsApp Support',
    ],
  },
  {
    id: 3,
    name: 'Pro',
    price: 1000,
    billing: 'Per Term',
    popular: false,
    features: [
      'Up to 1,500 Students',
      'Up to 100 Teachers',
      '5 School Administrators',
      'Everything in Basic',
      'Advanced Finance Features',
      'Advanced Reports & Analytics',
      'Bulk Operations',
      'Priority Support',
      'More Storage',
    ],
  },
  {
    id: 4,
    name: 'Enterprise',
    price: 2000,
    billing: 'Per Term',
    popular: false,
    features: [
      'Unlimited Students',
      'Unlimited Teachers',
      'Unlimited Administrators',
      'Everything in Pro',
      'Dedicated Onboarding & Training',
      'Custom Reports',
      'Dedicated Account Manager',
      'Highest Priority Support',
      'Maximum Storage Allocation',
    ],
  },
];

export default function PricingModal({ isOpen, onClose }) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSelectPlan = (plan) => {
    onClose();
    setTimeout(() => {
      // Pass numeric plan_id to checkout
      navigate(`/app/checkout?planId=${plan.id}`);
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-white overflow-auto">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-50 p-3 rounded-full hover:bg-slate-100 transition-colors"
      >
        <XMarkIcon className="w-8 h-8 text-slate-600" />
      </button>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-800 tracking-tight">
            Choose Your Plan
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Select a plan to activate your school account
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white border rounded-3xl p-8 flex flex-col h-full transition-all hover:shadow-2xl ${
                plan.popular
                  ? 'border-primary-500 ring-2 ring-primary-100 scale-[1.02]'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-500 to-secondary-600 text-white text-xs font-bold px-6 py-1.5 rounded-full shadow-md">
                  POPULAR
                </span>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-800">{plan.name}</h3>
                <div className="mt-6 flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-extrabold text-primary-600">
                    {plan.price}
                  </span>
                  <span className="text-slate-400 font-medium text-xl">SLE</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{plan.billing}</p>
              </div>

              <ul className="space-y-4 flex-1 mb-10">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan)}
                className={`w-full py-4 rounded-2xl font-semibold text-base transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white hover:shadow-lg'
                    : 'border-2 border-slate-300 hover:border-primary-500 text-slate-700 hover:text-primary-600'
                }`}
              >
                {plan.popular ? 'Select & Continue' : `Select ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-16 max-w-lg mx-auto">
          <p className="text-slate-500 leading-relaxed">
            All plans include core school management features. 
            <span className="font-medium text-slate-600"> You can upgrade or change your plan anytime.</span>
          </p>
        </div>
      </div>
    </div>
  );
}